import ClearIcon from '@mui/icons-material/Clear'
import { IconButton, InputAdornment, Stack } from '@mui/material'
import TextField, { TextFieldProps } from '@mui/material/TextField'
import {
  DatePicker as MuiDatePicker,
  DatePickerProps as MuiDatePickerProps,
} from '@mui/x-date-pickers/DatePicker'
import { FieldProps, getIn } from 'formik'
import { createErrorHandler } from './errorHandler'

export interface DatePickerProps
  extends FieldProps,
    Omit<MuiDatePickerProps<any, any>, 'name' | 'value' | 'error'> {
  textField?: TextFieldProps
  inlineClearButton?: boolean
}

export function fieldToDatePicker({
  field: { onChange: _onChange, ...field },
  form: { isSubmitting, touched, errors, setFieldValue, setFieldError, setFieldTouched },
  inlineClearButton,
  textField: { helperText, onBlur, ...textField } = {},
  disabled,
  label,
  onChange,
  onError,
  renderInput,
  ...props
}: DatePickerProps): MuiDatePickerProps<any, any> {
  const fieldError = getIn(errors, field.name)
  const showError = getIn(touched, field.name) && !!fieldError

  return {
    renderInput:
      renderInput ??
      ((params: TextFieldProps) => (
        <TextField
          {...params}
          error={showError}
          helperText={showError ? fieldError : helperText}
          label={label}
          onBlur={
            onBlur ??
            function () {
              setFieldTouched(field.name, true, true)
            }
          }
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <Stack direction="row" alignItems="center">
                {!!inlineClearButton && (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setFieldValue(field.name, null, true)}>
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                )}

                {params.InputProps?.endAdornment}
              </Stack>
            ),
          }}
          {...textField}
        />
      )),
    disabled: disabled ?? isSubmitting,
    onChange:
      onChange ??
      function (date: string) {
        // Do not switch this order, otherwise you might cause a race condition
        // See https://github.com/formium/formik/issues/2083#issuecomment-884831583
        setFieldTouched(field.name, true, false)
        setFieldValue(field.name, date, true)
      },
    onError: onError ?? createErrorHandler(fieldError, field.name, setFieldError),
    ...field,
    ...props,
  }
}

export function DatePicker({ children, ...props }: DatePickerProps) {
  return <MuiDatePicker {...fieldToDatePicker(props)}>{children}</MuiDatePicker>
}

DatePicker.displayName = 'DatePickerField'
